import { useState } from "react";
import {
  useGetMany,
  useListContext,
  useNotify,
  useRefresh,
  useUnselectAll,
} from "ra-core";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";

import { skipTraceOne, type SkipTraceOutcome } from "./skipTrace";
import type { Prospect } from "../types";

/**
 * Bulk skip trace from the List view. Routes each selected prospect through the
 * shared skipTraceOne(): source-backed leads queue via Airtable/Make; manual
 * prospects are traced directly via Endato and written to Supabase.
 *
 * Prospects that were already skip traced are pulled out of the batch and
 * never re-traced — re-tracing requires explicit authorization via the
 * per-prospect button.
 */
export const ProspectBulkSkipTrace = () => {
  const { selectedIds = [] } = useListContext<Prospect>();
  const { data: records = [] } = useGetMany<Prospect>(
    "prospects",
    { ids: selectedIds },
    { enabled: selectedIds.length > 0 },
  );
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll("prospects");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!records.length || busy) return;
    setBusy(true);
    // Pull out already-traced prospects — never re-trace them from a bulk run.
    const alreadyTraced = records.filter((p) => p.skip_traced_at);
    const todo = records.filter((p) => !p.skip_traced_at);
    if (!todo.length) {
      notify(
        `All ${alreadyTraced.length} selected were already skip traced — nothing run. Re-trace individually from the prospect page.`,
        { type: "warning" },
      );
      unselectAll();
      setBusy(false);
      return;
    }
    const tally: Record<SkipTraceOutcome, number> = {
      traced: 0,
      queued: 0,
      noMatch: 0,
      skipped: 0,
      failed: 0,
    };
    for (const p of todo) {
      tally[await skipTraceOne(p)]++;
    }
    const parts: string[] = [];
    if (tally.traced) parts.push(`${tally.traced} traced`);
    if (tally.queued) parts.push(`${tally.queued} queued`);
    if (tally.noMatch) parts.push(`${tally.noMatch} no match`);
    if (tally.skipped) parts.push(`${tally.skipped} skipped (no address)`);
    if (tally.failed) parts.push(`${tally.failed} failed`);
    if (alreadyTraced.length)
      parts.push(`${alreadyTraced.length} excluded (already traced)`);
    notify(`Skip trace: ${parts.join(" · ") || "nothing to do"}`, {
      type: tally.failed ? "warning" : "info",
    });
    refresh();
    unselectAll();
    setBusy(false);
  };

  return (
    <Button variant="outline" size="sm" onClick={run} disabled={busy}>
      <Search className="size-4 mr-1" /> {busy ? "Skip tracing…" : "Skip trace"}
    </Button>
  );
};
